import { useCallback } from 'react';
import { postMessage, checkPostMessageAvailable } from 'utils/appPostMessage';
import { setToastAppear } from 'components/Toast/Toast';
import { compareVersion } from 'utils';

// App 回傳「已收到請求」訊號的等待上限（毫秒）
// 逾時視為舊版 App 不支援 PIN 驗證，直接略過
const READY_TIMEOUT_MS = 3000;

// 支援交易 PIN 驗證的最低 App 版本
const MIN_SUPPORTED_VERSION = '4.14.0';

type PinResultType = 'success' | 'cancel' | 'unlogin' | 'identity_mismatch' | 'unsupported';

// 僅接受 x.y.z 形式的版本字串
const APP_VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

// 讀取一次 appVersion 後快取，保留 localStorage 供其他流程重複使用
let _cachedAppVersion: string | null | undefined = undefined;
function getAppVersion(): string | null {
  if (_cachedAppVersion === undefined) {
    _cachedAppVersion = window.localStorage.getItem('appVersion');
  }
  return _cachedAppVersion;
}

/**
 * 判斷是否達到支援交易 PIN 的最低版本。
 * 若無版本資訊或格式不符（舊版 App），視為不支援，略過 PIN 驗證。
 */
function isSupportedVersion(): boolean {
  const version = getAppVersion();
  if (!version || !APP_VERSION_PATTERN.test(version)) return false;
  return compareVersion(version, MIN_SUPPORTED_VERSION) >= 0;
}

/**
 * 交易 PIN 驗證 Hook。
 * 透過兩段式事件協議向 App 請求 PIN 驗證：
 * 1. 發送 transactionPinRequest，等待 AppTransactionPinReady（逾時視為舊版 App，略過）
 * 2. 等待 AppTransactionPinResult 取得驗證結果
 */
export function useTransactionPin() {
  const requestTransactionPin = useCallback((): Promise<PinResultType> => {
    return new Promise((resolve) => {
      // 經討論，逾時視為舊版 App，略過驗證
      const readyTimer = setTimeout(() => {
        window.removeEventListener('AppTransactionPinReady', onReady);
        resolve('unsupported');
      }, READY_TIMEOUT_MS);

      const onReady = () => {
        clearTimeout(readyTimer);
        window.addEventListener(
          'AppTransactionPinResult',
          (event: Event) => {
            const customEvent = event as CustomEvent<{ resultType: PinResultType }>;
            resolve(customEvent.detail?.resultType ?? 'cancel');
          },
          { once: true }
        );
      };

      window.addEventListener('AppTransactionPinReady', onReady, { once: true });

      const memberId = window.localStorage.getItem('ServiceAccountAId') ?? '';
      postMessage({ type: 'transactionPinRequest', memberId });
    });
  }, []);

  /**
   * 執行交易 PIN 驗證，回傳是否可繼續付款流程。
   * - 非 App 環境、success、unsupported：回傳 true
   * - cancel、unlogin、identity_mismatch：顯示提示後回傳 false
   */
  const verifyTransactionPin = useCallback(async (): Promise<boolean> => {
    // 經討論，舊版 App 不支援 postMessage，略過交易 PIN 驗證
    if (!checkPostMessageAvailable()) {
      return true;
    }

    // 經討論，App 版本未達支援門檻，略過交易 PIN 驗證
    if (!isSupportedVersion()) {
      return true;
    }

    const resultType = await requestTransactionPin();

    if (resultType === 'success' || resultType === 'unsupported') {
      return true;
    }

    const ALERT_MESSAGES: Partial<Record<PinResultType, string>> = {
      cancel: '交易密碼驗證已取消，請再試一次',
      unlogin: '請重新登入 App 後再試',
      identity_mismatch: '驗證身份不符，請重新登入 App 後再試',
    };

    setToastAppear(ALERT_MESSAGES[resultType] ?? '交易密碼驗證失敗，請稍後再試');
    return false;
  }, [requestTransactionPin]);

  return { verifyTransactionPin };
}
